import React, { useState, useEffect } from 'react';
import { FaTimes, FaUpload, FaFile } from 'react-icons/fa';

const API_URL = '/api';

const initialFields = {
  vendor_code: '', vendor_name: '', vendor_type: '', country: '', gst_number: '', pan_number: '', status: 'Active',
  contact_person_name: '', email: '', phone_number: '',
  registered_address: '', city: '', state: '', zip_code: '',
  account_name: '', account_number: '', ifsc_code: '', bank_name: '', bank_branch: '',
  logo: null, gst_certificate: null, company_reg_certificate: null, nda_contract_file: null
};

const VendorFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [filePreview, setFilePreview] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add this to prevent duplicate submissions

  // Fetch vendor types
  useEffect(() => {
    setIsLoadingTypes(true);
    fetch(`${API_URL}/vendor-types`)
      .then(res => res.json())
      .then(data => {
        if (data.vendorTypes) {
          setVendorTypes(data.vendorTypes);
        }
      })
      .catch(error => {
        console.error('Error fetching vendor types:', error);
      })
      .finally(() => {
        setIsLoadingTypes(false);
      });
  }, []);

  // Fetch current user
  useEffect(() => {
    fetch(`${API_URL}/current-user`)
      .then(res => res.json())
      .then(data => {
        if (data.user) {
          setCurrentUser(data.user);
        }
      })
      .catch(error => {
        console.error('Error fetching current user:', error);
      });
  }, []);

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFields({ ...initialFields, ...initialData });
        setSelectedVendor(initialData);
      } else {
        // Fetch next vendor code when creating new vendor
        setIsLoadingCode(true);
        fetch(`${API_URL}/vendorlatestcode`)
          .then(res => {
            if (!res.ok) {
              throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.json();
          })
          .then(data => {
            console.log('Received vendor code:', data);
            if (data && data.nextCode) {
              setFields(prev => ({ ...prev, vendor_code: data.nextCode }));
            } else {
              console.error('No nextCode in response:', data);
              setFields(prev => ({ ...prev, vendor_code: 'VAN-01' }));
            }
          })
          .catch(error => {
            console.error('Error fetching next vendor code:', error);
            setFields(prev => ({ ...prev, vendor_code: 'VAN-01' }));
          })
          .finally(() => {
            setIsLoadingCode(false);
          });
      }
      setErrors({});
      setIsSubmitting(false); // Reset submission state when modal opens
    }
  }, [open, initialData, isEdit]);

  const handleChange = e => {
    setFields({ ...fields, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFields(prev => ({
        ...prev,
        [fieldName]: file
      }));
      setFilePreview(prev => ({
        ...prev,
        [fieldName]: URL.createObjectURL(file)
      }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!fields.vendor_name) errs.vendor_name = 'Required';
    if (!fields.vendor_type) errs.vendor_type = 'Required';
    if (!fields.country) errs.country = 'Required';
    if (!fields.email) errs.email = 'Required';
    if (!fields.status) errs.status = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isSubmitting || formLoading) {
      console.log('Form submission already in progress, ignoring duplicate submission');
      return;
    }
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    setFormLoading(true);
    
    const formData = new FormData();
    
    // Add all text fields
    Object.keys(fields).forEach(key => {
      if (key !== 'logo' && key !== 'gst_certificate' && key !== 'company_reg_certificate' && key !== 'nda_contract_file') {
        formData.append(key, fields[key]);
      }
    });

    // Add files if they exist
    if (fields.logo) {
      formData.append('logo', fields.logo);
    }
    if (fields.gst_certificate) {
      formData.append('gst_certificate', fields.gst_certificate);
    }
    if (fields.company_reg_certificate) {
      formData.append('company_reg_certificate', fields.company_reg_certificate);
    }
    if (fields.nda_contract_file) {
      formData.append('nda_contract_file', fields.nda_contract_file);
    }

    // Add user ID
    formData.append('created_by', currentUser?.UserID ? currentUser.UserID : 1);
    formData.append('updated_by', currentUser?.UserID ? currentUser.UserID : 1);
    
    try {
      console.log('Submitting form data:', {
        isEdit,
        vendorCode: isEdit ? selectedVendor?.vendor_code : null,
        fields: Object.fromEntries(formData.entries())
      });

      let response;
      if (isEdit && selectedVendor) {
        console.log('Updating vendor:', selectedVendor.vendor_code);
        response = await fetch(`${API_URL}/vendors/${selectedVendor.vendor_code}`, {
          method: 'PUT',
          body: formData,
        });
      } else {
        console.log('Creating new vendor');
        response = await fetch(`${API_URL}/vendors`, {
          method: 'POST',
          body: formData,
        });
      }

      if (!response.ok) {
  const errorData = await response.json();
  console.error('Server response:', {
    status: response.status,
    statusText: response.statusText,
    error: errorData
  });

  // Handle duplicate vendor code error by refreshing the code
  if (response.status === 409 && errorData.error === 'DUPLICATE_VENDOR_CODE') {
    console.log('Duplicate vendor code detected, refreshing code...');
    // ... your existing retry logic ...
    return;
  }

  // ✅ Handle duplicate email
  if (response.status === 409 && errorData.error === 'DUPLICATE_EMAIL') {
    setErrors(prev => ({ ...prev, email: 'This email already exists' }));
    setIsSubmitting(false);
    setFormLoading(false);
    return; // stop here, don’t throw error
  }

  throw new Error(errorData.message || `Failed to ${isEdit ? 'update' : 'create'} vendor`);
}


      const result = await response.json();
      console.log('Server response:', result);
      
      // Call onSubmit only after successful submission
      onSubmit(formData);
    } catch (error) {
      console.error('Error saving vendor:', error);
      alert(error.message || 'An error occurred while saving the vendor');
    } finally {
      setFormLoading(false);
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
   <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4">
    <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full max-w-5xl p-4 sm:p-8 relative overflow-y-auto max-h-[90vh]">
        <button className="absolute top-4 right-4 text-[#2b6777] hover:text-[#52ab98]" onClick={onClose}><FaTimes size={20} /></button>
        <h2 className="text-2xl font-bold text-[#2b6777] mb-6 text-center">{isEdit ? 'Edit Vendor' : 'Create New Vendor'}</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Vendor Information */}
          <div className='bg-white p-4 rounded-xl shadow-md'>
            <h3 className="text-lg font-bold text-[#2b6777] mb-2">Vendor Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block w-1/4 text-sm text-center font-medium text-[#2b6777] whitespace-nowrap">Vendor Code</label>
                <input 
                  name="vendor_code" 
                  value={isLoadingCode ? 'Loading...' : fields.vendor_code} 
                  readOnly 
                  className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-700" 
                />
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Name *</label>
                <input 
                  name="vendor_name" 
                  autoComplete="off"
                  value={fields.vendor_name} 
                  onChange={handleChange} 
                  className={`w-full border rounded px-3 py-2 ${errors.vendor_name ? 'border-red-400' : 'border-[#c8d8e4]'}`} 
                />
                {errors.vendor_name && <span className="text-xs text-red-500">{errors.vendor_name}</span>}
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Type *</label>
                <select 
                  name="vendor_type" 
                  value={fields.vendor_type} 
                  onChange={handleChange} 
                  className={`w-full border rounded px-3 py-2 ${errors.vendor_type ? 'border-red-400' : 'border-[#c8d8e4]'}`}
                  disabled={isLoadingTypes}
                >
                  <option value="">Select Vendor Type</option>
                  {vendorTypes.map(type => (
                    <option key={type.type_id} value={type.type_name}>
                      {type.type_name}
                    </option>
                  ))}
                </select>
                {errors.vendor_type && <span className="text-xs text-red-500">{errors.vendor_type}</span>}
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Country *</label>
                <input name="country"   autoComplete="off"
 value={fields.country} onChange={handleChange} className={`w-full border rounded px-3 py-2 ${errors.country ? 'border-red-400' : 'border-[#c8d8e4]'}`} />
                {errors.country && <span className="text-xs text-red-500">{errors.country}</span>}
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">GST No.</label>
                <input name="gst_number"   autoComplete="off"
 value={fields.gst_number} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">PAN No.</label>
                <input name="pan_number"   autoComplete="off"
 value={fields.pan_number} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Status *</label>
                <select name="status" value={fields.status} onChange={handleChange} className={`w-full border rounded px-3 py-2 ${errors.status ? 'border-red-400' : 'border-[#c8d8e4]'}`}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                {errors.status && <span className="text-xs text-red-500">{errors.status}</span>}
              </div>
            </div>
          </div>
          {/* Contact Information */}
          <div className='bg-white p-4 rounded-xl shadow-md'>
            <h3 className="text-lg font-bold text-[#2b6777] mt-4 mb-2">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Contact Person</label>
                <input name="contact_person_name"   autoComplete="off"
 value={fields.contact_person_name} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Email *</label>
                <input name="email" value={fields.email}   autoComplete="off"
 onChange={handleChange} className={`w-full border rounded px-3 py-2 ${errors.email ? 'border-red-400' : 'border-[#c8d8e4]'}`} />
  {errors.email && <span className="text-xs text-red-500">{errors.email}</span>}
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Phone No.</label>
                <input name="phone_number"   autoComplete="off"
 value={fields.phone_number} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
            </div>
          </div>
          {/* Address Information */}
          <div className='bg-white p-4 rounded-xl shadow-md'>
            <h3 className="text-lg font-bold text-[#2b6777] mt-4 mb-2">Address Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Address</label>
                <input name="registered_address"   autoComplete="off"
 value={fields.registered_address} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">City</label>
                <input name="city"   autoComplete="off"
 value={fields.city} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">State</label>
                <input name="state"   autoComplete="off"
 value={fields.state} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Zip Code</label>
                <input name="zip_code"   autoComplete="off"
 value={fields.zip_code} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
            </div>
          </div>
          {/* Bank Information */}
          <div className='bg-white p-4 rounded-xl shadow-md'>
            <h3 className="text-lg font-bold text-[#2b6777] mb-4 mt-2">Bank Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Account No.</label>
                <input name="account_number"   autoComplete="off"
 value={fields.account_number} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Account Name</label>
                <input name="account_name"   autoComplete="off"
 value={fields.account_name} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">IFSC Code</label>
                <input name="ifsc_code"   autoComplete="off"
 value={fields.ifsc_code} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Bank Name</label>
                <input name="bank_name"   autoComplete="off"
 value={fields.bank_name} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Bank Branch</label>
                <input name="bank_branch"   autoComplete="off"
 value={fields.bank_branch} onChange={handleChange} className="w-full border border-[#c8d8e4] rounded px-3 py-2" />
              </div>
            </div>
          </div>
          {/* Documents Section */}
          <div className='bg-white p-4 rounded-xl shadow-md'>
            <h3 className="text-lg font-bold text-[#2b6777] mb-4">Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className='flex flex-col gap-2'>
                <label className="block text-sm font-medium text-[#2b6777]">Company Logo</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'logo')}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    className="flex items-center gap-2 px-4 py-2 bg-[#52ab98] text-white rounded cursor-pointer hover:bg-[#2b6777]"
                  >
                    <FaUpload /> Upload Logo
                  </label>
                  {filePreview.logo && (
                    <img src={filePreview.logo} alt="Logo preview" className="w-10 h-10 object-cover rounded" />
                  )}
                </div>
              </div>

              <div className='flex flex-col gap-2'>
                <label className="block text-sm font-medium text-[#2b6777]">GST Certificate</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange(e, 'gst_certificate')}
                    className="hidden"
                    id="gst-upload"
                  />
                  <label
                    htmlFor="gst-upload"
                    className="flex items-center gap-2 px-4 py-2 bg-[#52ab98] text-white rounded cursor-pointer hover:bg-[#2b6777]"
                  >
                    <FaUpload /> Upload GST
                  </label>
                  {filePreview.gst_certificate && (
                    <FaFile className="text-[#2b6777] text-xl" />
                  )}
                </div>
              </div>

              <div className='flex flex-col gap-2'>
                <label className="block text-sm font-medium text-[#2b6777]">Company Registration</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange(e, 'company_reg_certificate')}
                    className="hidden"
                    id="company-reg-upload"
                  />
                  <label
                    htmlFor="company-reg-upload"
                    className="flex items-center gap-2 px-4 py-2 bg-[#52ab98] text-white rounded cursor-pointer hover:bg-[#2b6777]"
                  >
                    <FaUpload /> Upload Registration
                  </label>
                  {filePreview.company_reg_certificate && (
                    <FaFile className="text-[#2b6777] text-xl" />
                  )}
                </div>
              </div>

              <div className='flex flex-col gap-2'>
                <label className="block text-sm font-medium text-[#2b6777]">NDA Contract</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange(e, 'nda_contract_file')}
                    className="hidden"
                    id="nda-upload"
                  />
                  <label
                    htmlFor="nda-upload"
                    className="flex items-center gap-2 px-4 py-2 bg-[#52ab98] text-white rounded cursor-pointer hover:bg-[#2b6777]"
                  >
                    <FaUpload /> Upload NDA
                  </label>
                  {filePreview.nda_contract_file && (
                    <FaFile className="text-[#2b6777] text-xl" />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-4 mt-6">
            <button type="button" className="px-4 py-2 rounded bg-gray-200 text-[#2b6777] font-semibold" onClick={onClose}>Cancel</button>
            <button 
              type="submit" 
              className="px-6 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white font-semibold shadow disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={formLoading || isSubmitting}
            >
              {formLoading || isSubmitting ? 'Saving...' : (isEdit ? 'Update Vendor' : 'Create Vendor')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorFormModal; 